import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { isNewKey } from '@ricoh/ria.client.core/utils/data/key';

import type { Batch, BatchFieldWithValue, BatchStatus, BatchView, Document, ProcessStage, ProfileSettings } from '~/common/model/api/batch';
import type { DocumentOptions } from '~/common/model/api/batch-type/process-flow';

import { PROCESS_STAGE_MAP_STATUS } from '~/common/constants/feature/process/batch';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';

import type { BatchFieldsFormField, ModifierAction } from './_batch.type';

import { createDocumentName, getCurrentDocumentNumber } from './_document';

const DOCUMENT_ACTIONS: Record<DocumentOptions, ModifierAction[]> = {
  none: [],
  classification: ['document.type'],
  assembly: ['document.move', 'document.merge', 'document.split', 'page.move', 'page.rotate'],
  delete: ['document.delete', 'page.delete'],
  duplicate: ['page.duplicate'],
  edit: ['batch.editFields'],
  reassignment: ['batch.reassignment'],
  rejection: ['batch.reject'],
  priority: ['batch.priority'],
  scanning: ['document.scanning', 'document.add', 'page.insert'],
  upload: ['document.upload', 'document.add', 'page.insert'],
};

/**
 * Check batch is offline.
 *
 * @param {Batch | BatchView | undefined} batch Batch.
 * @param {ProfileSettings} profiles Profile settings.
 *
 * @returns {boolean} True if batch is offline, otherwise false.
 */
export function isBatchOffline(batch: Batch | BatchView | undefined, profiles: ProfileSettings): boolean {
  const batchOptions = (batch && 'batchOptions' in batch ? batch.batchOptions : profiles.batchOptions) ?? [];
  const isOffline = batchOptions.includes('offline');

  // * Batch offline has client new key id
  if (batch) {
    return isNewKey(batch.id) && isOffline;
  }

  return isOffline;
}

/**
 * Get ProcessStage from BatchStatus.
 *
 * @param {BatchStatus} status Status.
 *
 * @returns {Nullable<ProcessStage>} ProcessStage.
 */
export function getProcessStage(status: BatchStatus): Nullable<ProcessStage> {
  const stage = Object.entries<ProcessStage, BatchStatus[]>(PROCESS_STAGE_MAP_STATUS).find(([_, statuses]) => statuses.includes(status));
  return stage?.[0];
}

/**
 * Add Documents to Batch.
 *
 * @param {Batch | BatchView} batch Batch.
 * @param {Document[]} documents Documents to add.
 * @param {string} documentNamePrefix Document name prefix.
 * @param {Func<[document: Document], boolean>} predicate Predicate.
 *
 * @returns {Document[]} Added Document.
 */
export function addDocuments(
  batch: Batch | BatchView,
  documents: Document[],
  documentNamePrefix: string,
  predicate?: Func<[document: Document], boolean>,
): Document[] {
  const result: Document[] = [];

  let documentNumber = getCurrentDocumentNumber(batch, documentNamePrefix);
  documents.forEach((document, index) => {
    // * Not match predicate
    if (predicate && !predicate(document)) {
      return;
    }

    result.push(document);

    // * Always increment
    documentNumber += 1;

    // * Set name
    document.name ??= createDocumentName(documentNamePrefix, documentNumber);

    // * Push to end if not has sourceRef
    if (!document.sourceRef) {
      batch.documents.push(document);
      return;
    }

    // **** Use prev document as reference position ********************

    // * Push to end with first document cause not have prev document
    if (index === 0) {
      batch.documents.push(document);
      return;
    }

    // * Push to end cause prev not has sourceRef
    const prevDocument = documents.at(index - 1);
    if (!prevDocument?.sourceRef) {
      batch.documents.push(document);
      return;
    }

    const prevSourceRef = prevDocument.sourceRef;

    // * Push to end cause prev document not found in batch
    const prevIndex = batch.documents.findLastIndex(({ sourceRef }) => sourceRef?.key === prevSourceRef.key && sourceRef?.index === prevSourceRef.index);
    if (prevIndex === -1) {
      batch.documents.push(document);
      return;
    }

    // * Add after prev document
    batch.documents.splice(prevIndex + 1, 0, document);
  });

  return result;
}

/**
 * Create BatchFieldsWithValue FormField.
 *
 * @param {BatchFieldWithValue[]} batchFields BatchFields with value.
 *
 * @returns {BatchFieldsFormField} BatchFieldsWithValue FormField.
 */
export function createBatchFieldsWithValueFormField(batchFields: BatchFieldWithValue[]): BatchFieldsFormField {
  return batchFields.reduce<BatchFieldsFormField>((prev, { id, type, value }) => {
    prev[id] = {
      isValidValue: !value?.metadata?.errors?.length,
      value: fieldLogic.createDataValueFormField(value, type),
    };

    return prev;
  }, {});
}

/**
 * Create BatchFieldsWithValue from FormField.
 *
 * @param {Batch} batch Batch.
 * @param {BatchFieldsFormField} batchFields BatchFields FormField.
 *
 * @returns {BatchFieldWithValue[]} BatchFieldsWithValue.
 */
export function createBatchFieldsWithValue(batch: Batch, batchFields: BatchFieldsFormField): BatchFieldWithValue[] {
  return batch.fields.map<BatchFieldWithValue>((batchField) => {
    const field = batchFields[batchField.id];
    if (!field) {
      throw new ClientError({
        module: 'logic',
        message: 'BatchField not found from FormField',
        cause: batchField,
      });
    }

    return {
      ...batchField,
      value: fieldLogic.createDataValue(batchField.type, field.value),
    };
  });
}

/**
 * Check batch has ModifierAction.
 *
 * @param {Nullable<Batch>} batch Batch.
 * @param {ProfileSettings} profiles Profile settings.
 * @param {ModifierAction} action Modifier action.
 *
 * @returns {boolean} Has Document Options with action.
 */
export function modifiable(batch: Nullable<Batch>, profiles: ProfileSettings, action: ModifierAction): boolean {
  const documentOptions = batch?.documentOptions ?? profiles.documentOptions ?? [];
  if (!documentOptions.length || documentOptions.includes('none')) {
    return false;
  }

  return Object.entries<DocumentOptions, ModifierAction[]>(DOCUMENT_ACTIONS).some(
    ([name, actions]) => actions.includes(action) && documentOptions.includes(name),
  );
}
